import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, CheckCircle, Flag, XCircle, AlertTriangle, Clock, HelpCircle, Check, Square } from 'lucide-react';

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 220px;
  z-index: 1000;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 2.5rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f3f4f6;
  }
`;

const StatusIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.color || '#6b7280'};
  display: flex;
  align-items: center;
`;

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const DropdownOption = styled.div`
  padding: 0.625rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  color: #374151;
  font-size: 0.875rem;

  &:hover {
    background: #f3f4f6;
  }

  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }
`;

const OptionIcon = styled.div`
  color: ${props => props.color || '#6b7280'};
  flex-shrink: 0;
`;

const OptionText = styled.div`
  flex-grow: 1;
  font-weight: 500;
`;

const CheckIconContainer = styled.div`
  color: #6b7280;
  flex-shrink: 0;
`;

const StatusDropdown = ({ 
  value, 
  onChange, 
  options = [
    { value: 'none', label: 'None', icon: Square, color: '#10b981' },
    { value: 'lead_review', label: 'Lead Review', icon: Flag, color: '#8b5cf6' },
    { value: 'client_review', label: 'Client Review', icon: Flag, color: '#f59e0b' },
    { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: '#3b82f6' },
    { value: 'pending', label: 'Pending', icon: Clock, color: '#8b5cf6' },
    { value: 'disputed', label: 'Disputed', icon: AlertTriangle, color: '#ef4444' },
    { value: 'unknown', label: 'Unknown', icon: HelpCircle, color: '#6b7280' },
    { value: 'ignore', label: 'Ignore', icon: XCircle, color: '#9ca3af' }
  ],
  disabled = false,
  placeholder = 'Select status...',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue) => {
    // Create a synthetic event object to match the expected interface
    const syntheticEvent = {
      target: {
        value: optionValue
      }
    };
    onChange(syntheticEvent);
    setIsOpen(false);
  };

  const getStatusIcon = (status) => {
    const option = options.find(opt => opt.value === status);
    return option ? option.icon : CheckCircle;
  };

  const getStatusColor = (status) => {
    const option = options.find(opt => opt.value === status);
    return option ? option.color : '#6b7280';
  };

  const StatusIconComponent = selectedOption ? selectedOption.icon : CheckCircle;

  return (
    <DropdownWrapper ref={dropdownRef} {...props}>
      <DropdownButton
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-cy="flag-field-btn"
      >
        <StatusIcon color={getStatusColor(value)}>
          <StatusIconComponent size={16} />
        </StatusIcon>
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronIconContainer>
          <ChevronDown size={16} />
        </ChevronIconContainer>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          {options.map((option) => {
            const OptionIconComponent = option.icon;
            return (
              <DropdownOption
                key={option.value}
                onClick={() => handleSelect(option.value)}
              >
                <OptionIcon color={option.color}>
                  <OptionIconComponent size={16} />
                </OptionIcon>
                <OptionText>{option.label}</OptionText>
                {value === option.value && (
                  <CheckIconContainer>
                    <Check size={16} />
                  </CheckIconContainer>
                )}
              </DropdownOption>
            );
          })}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

export default StatusDropdown;